import type { Operation } from '../sdk'
import { filterLocalPlayerOperations } from './operation-owner'

/**
 * Normalized fast-forward payload for store state application.
 * Accepts canonical and legacy backend payload shapes.
 *
 * Phase 2: Supports additive display-domain replay timestamp field.
 * When currentReplayTimeDisplay is present, it should be preferred as the barrier/refetch anchor.
 */
export interface NormalizedFastForwardResponse {
  operations: Operation[]
  currentReplayTime: string
  currentReplayTimeDisplay?: string
  remainingMatchTimeMs: number
  balance: number
  equity: number
}

/**
 * Payload shape variants that may come from backend.
 * Canonical shape: metrics, remainingTime (Go duration string)
 * Legacy shape: balance, equity, remainingMatchTime (milliseconds)
 * Phase 2: Optional additive currentReplayTimeDisplay for display-domain anchor
 */
interface CanonicalPayload {
  operations: Operation[]
  currentReplayTime: string
  currentReplayTimeDisplay?: string
  metrics?: {
    balance: string | number
    equity: string | number
  }
  remainingTime?: string
}

interface LegacyPayload {
  operations: Operation[]
  currentReplayTime: string
  currentReplayTimeDisplay?: string
  balance?: number
  equity?: number
  remainingMatchTime?: number
}

type FastForwardPayload = CanonicalPayload | LegacyPayload

function parseDecimalMetric(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

/**
 * Parses Go duration string format into milliseconds.
 * Examples: "1h0m0s", "1h5m20s", "30m0s", "45s", "0s"
 * Returns undefined if parse fails.
 */
function parseGoDuration(durationStr: string): number | undefined {
  if (!durationStr || typeof durationStr !== 'string') {
    return undefined
  }

  try {
    const durationRegex = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/
    const match = durationStr.match(durationRegex)

    if (!match || !durationStr.match(/[hms]/)) {
      return undefined
    }

    const hours = parseInt(match[1] || '0', 10)
    const minutes = parseInt(match[2] || '0', 10)
    const seconds = parseFloat(match[3] || '0')

    const totalMs = hours * 3600000 + minutes * 60000 + seconds * 1000

    return Math.max(0, totalMs)
  } catch {
    return undefined
  }
}

/**
 * Normalizes fast-forward payload from backend into frontend shape.
 *
 * Handles:
 * - Canonical `remainingTime` (Go duration string like "1h0m0s") → milliseconds
 * - Legacy `remainingMatchTime` (already in milliseconds) → used directly
 * - Canonical `metrics` object → extracted balance/equity
 * - Legacy direct balance/equity fields → used directly
 * - Phase 2: Optional `currentReplayTimeDisplay` as display-domain anchor (when present)
 * - Fallback: if canonical parse fails, uses legacy when present; emits diagnostics
 * - Filters operations to local player only (optional userId provided)
 *
 * @returns Normalized shape ready for store state, or undefined if critical fields missing
 */
export function normalizeFastForwardResponse(
  payload: any,
  localUserId?: string
): NormalizedFastForwardResponse | undefined {
  if (!payload || typeof payload !== 'object') {
    console.warn('[fast-forward-normalizer] Payload is not an object')
    return undefined
  }

  let { operations, currentReplayTime, currentReplayTimeDisplay } = payload

  if (!Array.isArray(operations)) {
    console.warn('[fast-forward-normalizer] operations is not an array')
    return undefined
  }

  // Filter operations to local player only
  if (localUserId) {
    operations = filterLocalPlayerOperations(operations, localUserId)
  }

  if (typeof currentReplayTime !== 'string') {
    console.warn('[fast-forward-normalizer] currentReplayTime is not a string')
    return undefined
  }

  // Normalize remaining time: try canonical format, fall back to legacy
  let remainingMatchTimeMs: number | undefined

  // Try canonical remainingTime (Go duration string)
  if (typeof payload.remainingTime === 'string') {
    remainingMatchTimeMs = parseGoDuration(payload.remainingTime)
    if (remainingMatchTimeMs === undefined) {
      // Parse failed, try legacy remainingMatchTime
      if (typeof payload.remainingMatchTime === 'number') {
        remainingMatchTimeMs = payload.remainingMatchTime
        console.warn(
          '[fast-forward-normalizer] Canonical remainingTime parse failed, falling back to legacy remainingMatchTime',
          { canonical: payload.remainingTime, legacy: remainingMatchTimeMs }
        )
      } else {
        console.warn('[fast-forward-normalizer] Canonical remainingTime parse failed and no legacy remainingMatchTime present', {
          canonical: payload.remainingTime,
        })
      }
    }
  } else if (typeof payload.remainingMatchTime === 'number') {
    // Direct legacy remainingMatchTime
    remainingMatchTimeMs = payload.remainingMatchTime
  }

  // Clamp to non-negative
  if (remainingMatchTimeMs === undefined) {
    remainingMatchTimeMs = 0
  } else {
    remainingMatchTimeMs = Math.max(0, remainingMatchTimeMs)
  }

  // Normalize balance/equity: try canonical metrics, fall back to legacy
  let balance: number | undefined
  let equity: number | undefined

  if (payload.metrics && typeof payload.metrics === 'object') {
    balance = parseDecimalMetric(payload.metrics.balance)
    equity = parseDecimalMetric(payload.metrics.equity)
  }

  // Fall back to legacy fields
  if (balance === undefined && typeof payload.balance === 'number') {
    balance = payload.balance
  }
  if (equity === undefined && typeof payload.equity === 'number') {
    equity = payload.equity
  }

  // Both balance and equity are required
  if (balance === undefined || equity === undefined) {
    console.warn('[fast-forward-normalizer] Missing balance or equity', {
      balance,
      equity,
      hasMetrics: !!payload.metrics,
      hasLegacyBalance: typeof payload.balance === 'number',
      hasLegacyEquity: typeof payload.equity === 'number',
    })
    return undefined
  }

  const result: NormalizedFastForwardResponse = {
    operations,
    currentReplayTime,
    remainingMatchTimeMs,
    balance,
    equity,
  }

  // Phase 2: Include optional display-domain replay timestamp if provided
  if (typeof currentReplayTimeDisplay === 'string') {
    result.currentReplayTimeDisplay = currentReplayTimeDisplay
  }

  return result
}
